/**
 * Where the segmented details go (tier 2, PLAN §6). Pure geometry on part boxes, no Three.js:
 * every joint becomes one or more *runs* (a detail instanced along a line at an exact length),
 * plus *caps* where a housed part's edge shows in the housing part's edge.
 *
 * Frames are in metres in the placement frame (floor / outer left / back). A run's frame is
 * X = along the run (origin at x = 0), Y = across, away from the entering part, Z = out of the
 * face it lies on; the kit pieces are authored in the same frame.
 */
import { boardFrame } from './board';
import type { KitAssemblyInput, KitJoint, KitPart } from './types';

export type Vec3 = [number, number, number];

export interface DetailRun {
  assetId: string;
  jointId: string;
  /** Metres. */
  origin: Vec3;
  x: Vec3;
  y: Vec3;
  z: Vec3;
  length: number;
  /** The part whose materials bind the piece's wood slots (lipping: the lipped part). */
  partId: string;
}

export interface HousingCap {
  jointId: string;
  /** Part whose edge shows (the entering part). */
  partId: string;
  /** Metres: the rectangle's centre, its two in-plane axes with half-sizes, and its normal. */
  centre: Vec3;
  u: Vec3;
  v: Vec3;
  halfU: number;
  halfV: number;
  normal: Vec3;
  /** World axis of `v` is the entering part's thickness: v runs face to face. */
  thicknessM: number;
  /** Sign along `u` of the housing's closed (deep) end; 0 when both ends are open (egg-crate, tenon). */
  deepU: number;
  /** Which of the entering part's materials shows: its edge (a crossing) or its end grain (a through tenon). */
  shows: 'edge' | 'end';
}

/** A mitred end of a board, in its local frame (board.ts): which end of L, how deep the cut runs
 * along L (metres, the other board's thickness), and which T face is the outside (full length). */
export interface MitreEnd {
  end: 1 | -1;
  depth: number;
  outer: 1 | -1;
}

interface Box {
  lo: Vec3;
  hi: Vec3;
}

const KIND_ASSET: Record<string, string> = {
  'housed-dado': 'detail.housed-dado',
  rebate: 'detail.rebate',
  'egg-crate': 'detail.egg-crate',
  'screwed-butt': 'detail.screwed-butt',
  'dowel-butt': 'detail.screwed-butt',
  'pocket-screw': 'detail.screwed-butt',
  'mortise-tenon': 'detail.mortise-tenon',
  'through-tenon': 'detail.mortise-tenon',
  mitre: 'detail.mitre',
};
/** Joint kinds whose entering part is drawn trimmed to the housing face (it is inside the other part). */
const TRIMMED = new Set(['housed-dado', 'rebate', 'mortise-tenon', 'through-tenon']);

/** Tolerance (mm) for "touching". */
const TOL = 0.5;
/** Caps and seams sit this far (m) proud of the face they decorate. */
export const PROUD = 0.00005;

export function boxOf(p: KitPart): Box {
  const q = p.placement;
  return { lo: [q.x, q.y, q.z], hi: [q.x + q.sx, q.y + q.sy, q.z + q.sz] };
}

function intersect(a: Box, b: Box, tol = 0): Box | null {
  const lo: Vec3 = [0, 0, 0];
  const hi: Vec3 = [0, 0, 0];
  for (let i = 0; i < 3; i++) {
    lo[i] = Math.max(a.lo[i], b.lo[i]);
    hi[i] = Math.min(a.hi[i], b.hi[i]);
    if (hi[i] < lo[i] - tol) return null;
    if (hi[i] < lo[i]) [lo[i], hi[i]] = [hi[i], lo[i]];
  }
  return { lo, hi };
}

const unit = (axis: number, sign = 1): Vec3 => {
  const v: Vec3 = [0, 0, 0];
  v[axis] = sign;
  return v;
};
const cross = (a: Vec3, b: Vec3): Vec3 => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
const mm = (v: Vec3): Vec3 => [v[0] / 1000, v[1] / 1000, v[2] / 1000];

/** One run along `run` from lo to hi (mm), lying at the given point in the other two axes. */
function lineRun(assetId: string, joint: KitJoint, partId: string, run: number, lo: number, hi: number, at: Vec3, y: Vec3, z: Vec3): DetailRun | null {
  const x = cross(y, z);
  const sign = x[run];
  if (Math.abs(sign) < 0.5 || hi - lo < 0.5) return null;
  const origin: Vec3 = [...at];
  origin[run] = sign > 0 ? lo : hi;
  const o = mm(origin);
  return { assetId, jointId: joint.id, origin: [o[0] + z[0] * PROUD, o[1] + z[1] * PROUD, o[2] + z[2] * PROUD], x, y, z, length: (hi - lo) / 1000, partId };
}

export interface JointGeometry {
  runs: DetailRun[];
  caps: HousingCap[];
  skipped: Array<{ jointId: string; reason: string }>;
}

export function jointGeometry(input: KitAssemblyInput): JointGeometry {
  const parts = new Map(input.parts.map((p) => [p.id, p]));
  const runs: DetailRun[] = [];
  const caps: HousingCap[] = [];
  const skipped: JointGeometry['skipped'] = [];
  for (const j of input.joints ?? []) {
    if (j.visible === false) {
      skipped.push({ jointId: j.id, reason: 'not visible' });
      continue;
    }
    const assetId = KIND_ASSET[j.kind];
    const a = parts.get(j.a);
    const b = parts.get(j.b);
    if (!assetId || !a || !b) {
      skipped.push({ jointId: j.id, reason: !assetId ? `no detail for ${j.kind}` : 'unknown part' });
      continue;
    }
    const A = boxOf(a);
    const B = boxOf(b);
    const R = intersect(A, B, TOL);
    if (!R) {
      skipped.push({ jointId: j.id, reason: 'parts do not meet' });
      continue;
    }
    const across = boardFrame(a).perm[2];
    const others = [0, 1, 2].filter((i) => i !== across);
    const run = (R.hi[others[0]] - R.lo[others[0]]) >= (R.hi[others[1]] - R.lo[others[1]]) ? others[0] : others[1];
    const k = others.find((i) => i !== run) as number;
    const ca = (A.lo[k] + A.hi[k]) / 2;
    const cb = (B.lo[k] + B.hi[k]) / 2;
    const sk = ca >= cb ? 1 : -1;
    const depth = R.hi[k] - R.lo[k];
    const runLo = R.lo[run];
    const runHi = R.hi[run];
    const kind = j.kind;
    const push = (r: DetailRun | null) => r && runs.push(r);

    if (kind === 'mitre') {
      runs.push(...mitreRuns(j, a, b));
      continue;
    }
    if (kind === 'egg-crate') {
      for (const kk of [B.lo[k], B.hi[k]])
        for (const [ac, ys] of [[A.lo[across], -1], [A.hi[across], 1]] as const) {
          const at: Vec3 = [0, 0, 0];
          at[k] = kk;
          at[across] = ac;
          push(lineRun(assetId, j, a.id, run, runLo, runHi, at, unit(across, ys), unit(k, kk === B.hi[k] ? 1 : -1)));
        }
    } else {
      const plane = sk > 0 ? B.hi[k] : B.lo[k];
      const faces: Array<[number, number]> = [[A.lo[across], -1], [A.hi[across], 1]];
      for (const [ac, ys] of faces) {
        const flushWithB = Math.abs(ac - B.lo[across]) < TOL || Math.abs(ac - B.hi[across]) < TOL;
        if (kind === 'rebate' && flushWithB) {
          // The panel's outer face is flush with the housing part: the seam is on that outer
          // plane, at the rebate shoulder inside the housing part.
          if (depth < TOL) continue;
          const shoulder = sk > 0 ? R.lo[k] : R.hi[k];
          const at: Vec3 = [0, 0, 0];
          at[k] = shoulder;
          at[across] = ac;
          push(lineRun(assetId, j, a.id, run, runLo, runHi, at, unit(k, -sk), unit(across, ys)));
          continue;
        }
        if (flushWithB) continue; // a face flush with the housing part's own face shows no shoulder
        const at: Vec3 = [0, 0, 0];
        at[k] = plane;
        at[across] = ac;
        push(lineRun(assetId, j, a.id, run, runLo, runHi, at, unit(across, ys), unit(k, sk)));
      }
      if (kind === 'mortise-tenon' || kind === 'through-tenon') {
        // the shoulder's two short sides, across the tenoned part's thickness
        for (const [rc, ys] of [[A.lo[run], -1], [A.hi[run], 1]] as const) {
          if (Math.abs(rc - B.lo[run]) < TOL || Math.abs(rc - B.hi[run]) < TOL) continue;
          const at: Vec3 = [0, 0, 0];
          at[k] = plane;
          at[run] = rc;
          push(lineRun(assetId, j, a.id, across, Math.max(A.lo[across], B.lo[across]), Math.min(A.hi[across], B.hi[across]), at, unit(run, ys), unit(k, sk)));
        }
      }
      if (kind === 'through-tenon') {
        // the tenon's end grain, flush in the far face of the mortised part, with two wedges
        const far = sk > 0 ? B.lo[k] : B.hi[k];
        const tA = A.hi[across] - A.lo[across];
        const wA = Math.min(A.hi[run], B.hi[run]) - Math.max(A.lo[run], B.lo[run]);
        const tenonT = Math.max(6, Math.round(tA / 3));
        // wide boards get twin tenons (each at most ~120 mm), shoulders of up to 12 mm at the edges
        const shoulder = Math.min(12, wA / 6);
        const count = wA - 2 * shoulder > 150 ? 2 : 1;
        const between = count > 1 ? Math.max(20, wA / 6) : 0;
        const tenonW = Math.max(10, (wA - 2 * shoulder - between * (count - 1)) / count);
        const runLo0 = Math.max(A.lo[run], B.lo[run]) + shoulder;
        const normal = unit(k, -sk);
        for (let n = 0; n < count; n++) {
          const c: Vec3 = [0, 0, 0];
          c[k] = far;
          c[across] = (A.lo[across] + A.hi[across]) / 2;
          c[run] = runLo0 + n * (tenonW + between) + tenonW / 2;
          const cm = mm(c);
          caps.push({
            jointId: j.id, partId: a.id, shows: 'end',
            centre: [cm[0] + normal[0] * PROUD * 2, cm[1] + normal[1] * PROUD * 2, cm[2] + normal[2] * PROUD * 2],
            u: unit(run), v: unit(across), halfU: tenonW / 2000, halfV: tenonT / 2000, normal, thicknessM: tenonT / 1000, deepU: 0,
          });
          for (const f of [0.25, 0.75]) {
            const at: Vec3 = [...c];
            at[run] = c[run] - tenonW / 2 + f * tenonW;
            const yv = unit(run, 1);
            const x = cross(yv, normal);
            const lo = c[across] - tenonT / 2;
            const hi = c[across] + tenonT / 2;
            const origin: Vec3 = [...at];
            origin[across] = x[across] > 0 ? lo : hi;
            const o = mm(origin);
            runs.push({ assetId: 'detail.tenon-wedge', jointId: j.id, origin: [o[0] + normal[0] * PROUD * 3, o[1] + normal[1] * PROUD * 3, o[2] + normal[2] * PROUD * 3], x, y: yv, z: normal, length: tenonT / 1000, partId: a.id });
          }
        }
      }
    }

    // Crossing caps (egg-crate only): at an exposed end of the crossing, the continuous part's
    // edge shows through the notched one, flush. Housed dadoes and rebates leave no cap: the
    // entering part is trimmed to the housing face (renderPlacements), so the housing part's
    // edge stays whole and only the hairline shoulders show.
    if (kind === 'egg-crate' && depth > TOL) {
      const thicknessM = (A.hi[across] - A.lo[across]) / 1000;
      for (const [end, sgn] of [[runLo, -1], [runHi, 1]] as const) {
        const exposed = Math.abs(end - (sgn < 0 ? B.lo[run] : B.hi[run])) < TOL && Math.abs(end - (sgn < 0 ? A.lo[run] : A.hi[run])) < TOL;
        if (!exposed) continue;
        const kLo = kind === 'egg-crate' ? R.lo[k] : Math.max(R.lo[k], B.lo[k]);
        const kHi = kind === 'egg-crate' ? R.hi[k] : Math.min(R.hi[k], B.hi[k]);
        const c: Vec3 = [0, 0, 0];
        c[run] = end;
        c[k] = (kLo + kHi) / 2;
        c[across] = (A.lo[across] + A.hi[across]) / 2;
        const normal = unit(run, sgn);
        const cm = mm(c);
        caps.push({
          jointId: j.id, partId: a.id,
          centre: [cm[0] + normal[0] * PROUD * 2, cm[1] + normal[1] * PROUD * 2, cm[2] + normal[2] * PROUD * 2],
          u: unit(k), v: unit(across), halfU: (kHi - kLo) / 2000, halfV: thicknessM / 2, normal, thicknessM,
          deepU: kind === 'egg-crate' ? 0 : -sk, shows: 'edge',
        });
      }
    }
  }
  return { runs, caps, skipped };
}

/** Lipping runs along the front (+z) long edge of every part whose edge text asks for lipping. */
export function lippingRuns(input: KitAssemblyInput, forThicknessMm: number, widthMm: number, wanted: (p: KitPart) => boolean): { runs: DetailRun[]; skipped: string[] } {
  const runs: DetailRun[] = [];
  const skipped: string[] = [];
  for (const p of input.parts) {
    if (!wanted(p)) continue;
    const f = boardFrame(p);
    const [iL, iW, iT] = f.perm;
    if (iW !== 2 || Math.abs(f.size[2] * 1000 - forThicknessMm) > 0.6) {
      skipped.push(p.id);
      continue;
    }
    const B = boxOf(p);
    const z: Vec3 = [0, 0, 1];
    const y = unit(iT);
    const x = cross(y, z);
    const at: Vec3 = [0, 0, 0];
    at[iT] = B.lo[iT];
    at[2] = B.hi[2] - widthMm + PROUD * 1000;
    at[iL] = x[iL] > 0 ? B.lo[iL] : B.hi[iL];
    runs.push({ assetId: 'detail.lipping-end', jointId: `lipping:${p.id}`, origin: mm(at), x, y, z, length: (B.hi[iL] - B.lo[iL]) / 1000, partId: p.id });
  }
  return { runs, skipped };
}

/**
 * The boxes to draw. A housed dado or a rebate puts part of the entering part inside the housing
 * part; drawn as is, its faces would be coplanar with the housing part's edge and fight it, and
 * an eased arris would poke through. So the entering part is drawn trimmed to the housing face
 * (what is seen of a stopped housing). A board that passes right through the housing part, carrying on
 * beyond both its faces (a staggered shelf through its continuous divider), is drawn whole. A lipped part is drawn without the front band its lipping
 * replaces. The manufacturing placement is untouched; this is display only.
 */
export function renderPlacements(input: KitAssemblyInput, lipping?: { widthMm: number; forThicknessMm: number; wanted: (p: KitPart) => boolean }): Map<string, KitPart['placement']> {
  const out = new Map(input.parts.map((p) => [p.id, { ...p.placement }]));
  const parts = new Map(input.parts.map((p) => [p.id, p]));
  for (const j of input.joints ?? []) {
    const a = parts.get(j.a);
    const b = parts.get(j.b);
    if (!a || !b) continue;
    if (j.kind === 'mitre') {
      // both boards must cover the corner square; a butt-laid corner is extended into it
      for (const [p, q] of [[a, b], [b, a]] as const) {
        const iL = boardFrame(p).perm[0];
        const P = out.get(p.id)!;
        const lo = [P.x, P.y, P.z];
        const hi = [P.x + P.sx, P.y + P.sy, P.z + P.sz];
        const Q = boxOf(q);
        if (Math.abs(hi[iL] - Q.lo[iL]) < TOL) hi[iL] = Q.hi[iL];
        else if (Math.abs(lo[iL] - Q.hi[iL]) < TOL) lo[iL] = Q.lo[iL];
        out.set(p.id, { x: lo[0], y: lo[1], z: lo[2], sx: hi[0] - lo[0], sy: hi[1] - lo[1], sz: hi[2] - lo[2] });
      }
      continue;
    }
    if (!TRIMMED.has(j.kind)) continue;
    const A = boxOf(a);
    const B = boxOf(b);
    const R = intersect(A, B, 0);
    if (!R || [0, 1, 2].some((i) => R.hi[i] - R.lo[i] <= 0.05)) continue;
    const across = boardFrame(a).perm[2];
    const others = [0, 1, 2].filter((i) => i !== across);
    const run = (R.hi[others[0]] - R.lo[others[0]]) >= (R.hi[others[1]] - R.lo[others[1]]) ? others[0] : others[1];
    const k = others.find((i) => i !== run) as number;
    // a board that crosses right through the housing part (a staggered shelf through its continuous divider)
    // carries on beyond both faces: trimming it would delete the board on one side, so it is drawn whole
    if (A.lo[k] < B.lo[k] - TOL && A.hi[k] > B.hi[k] + TOL) continue;
    const q = out.get(a.id)!;
    const lo = [q.x, q.y, q.z];
    const hi = [q.x + q.sx, q.y + q.sy, q.z + q.sz];
    if ((B.lo[k] + B.hi[k]) / 2 < (A.lo[k] + A.hi[k]) / 2) lo[k] = Math.max(lo[k], R.hi[k]);
    else hi[k] = Math.min(hi[k], R.lo[k]);
    out.set(a.id, { x: lo[0], y: lo[1], z: lo[2], sx: hi[0] - lo[0], sy: hi[1] - lo[1], sz: hi[2] - lo[2] });
  }
  if (lipping) {
    for (const p of input.parts) {
      if (!lipping.wanted(p)) continue;
      const f = boardFrame(p);
      if (f.perm[1] !== 2 || Math.abs(f.size[2] * 1000 - lipping.forThicknessMm) > 0.6) continue;
      const q = out.get(p.id)!;
      out.set(p.id, { ...q, sz: q.sz - lipping.widthMm });
    }
  }
  return out;
}

/** Corner square of a mitre joint, after renderPlacements' extension (mm). */
function mitreCorner(a: KitPart, b: KitPart, shownA: Box = boxOf(a), shownB: Box = boxOf(b)): Box | null {
  const R = intersect(shownA, shownB, TOL);
  if (!R) return null;
  return R;
}

/** The mitred ends of every board, in each board's local frame (for board.ts). */
export function mitreEnds(input: KitAssemblyInput, shown?: Map<string, KitPart['placement']>): Map<string, MitreEnd[]> {
  const out = new Map<string, MitreEnd[]>();
  const parts = new Map(input.parts.map((p) => [p.id, p]));
  const boxShown = (p: KitPart): Box => {
    const q = shown?.get(p.id) ?? p.placement;
    return { lo: [q.x, q.y, q.z], hi: [q.x + q.sx, q.y + q.sy, q.z + q.sz] };
  };
  for (const j of input.joints ?? []) {
    if (j.kind !== 'mitre') continue;
    const a = parts.get(j.a);
    const b = parts.get(j.b);
    if (!a || !b) continue;
    const R = mitreCorner(a, b, boxShown(a), boxShown(b));
    if (!R) continue;
    for (const [p, q] of [[a, b], [b, a]] as const) {
      const P = boxShown(p);
      const Q = boxShown(q);
      const f = boardFrame({ ...p, placement: shown?.get(p.id) ?? p.placement });
      const [iL, , iT] = f.perm;
      const end: 1 | -1 = (R.lo[iL] + R.hi[iL]) / 2 > (P.lo[iL] + P.hi[iL]) / 2 ? 1 : -1;
      const outer: 1 | -1 = (Q.lo[iT] + Q.hi[iT]) / 2 < (P.lo[iT] + P.hi[iT]) / 2 ? 1 : -1;
      const list = out.get(p.id) ?? [];
      list.push({ end, depth: (R.hi[iL] - R.lo[iL]) / 1000, outer });
      out.set(p.id, list);
    }
  }
  return out;
}

/** Glue lines along the diagonal of a mitred corner, on both faces across the frame's depth. */
function mitreRuns(j: KitJoint, a: KitPart, b: KitPart): DetailRun[] {
  const A = boxOf(a);
  const B = boxOf(b);
  // the corner square as drawn: both boards extended to cover it
  const iLa = boardFrame(a).perm[0];
  const iLb = boardFrame(b).perm[0];
  const ext = (P: Box, Q: Box, iL: number): Box => {
    const lo: Vec3 = [...P.lo];
    const hi: Vec3 = [...P.hi];
    if (Math.abs(hi[iL] - Q.lo[iL]) < TOL) hi[iL] = Q.hi[iL];
    else if (Math.abs(lo[iL] - Q.hi[iL]) < TOL) lo[iL] = Q.lo[iL];
    return { lo, hi };
  };
  const R = intersect(ext(A, B, iLa), ext(B, A, iLb), TOL);
  if (!R || iLa === iLb) return [];
  const depth = [0, 1, 2].find((i) => i !== iLa && i !== iLb) as number;
  // outer corner: the far end of A along its length and of B along its length
  const ca = (A.lo[iLa] + A.hi[iLa]) / 2;
  const cb = (B.lo[iLb] + B.hi[iLb]) / 2;
  const outer: Vec3 = [0, 0, 0];
  const inner: Vec3 = [0, 0, 0];
  outer[iLa] = (R.lo[iLa] + R.hi[iLa]) / 2 > ca ? R.hi[iLa] : R.lo[iLa];
  inner[iLa] = outer[iLa] === R.hi[iLa] ? R.lo[iLa] : R.hi[iLa];
  outer[iLb] = (R.lo[iLb] + R.hi[iLb]) / 2 > cb ? R.hi[iLb] : R.lo[iLb];
  inner[iLb] = outer[iLb] === R.hi[iLb] ? R.lo[iLb] : R.hi[iLb];
  const runs: DetailRun[] = [];
  for (const [plane, sign] of [[R.hi[depth], 1], [R.lo[depth], -1]] as const) {
    const o: Vec3 = [...outer];
    const i: Vec3 = [...inner];
    o[depth] = plane;
    i[depth] = plane;
    const d: Vec3 = [i[0] - o[0], i[1] - o[1], i[2] - o[2]];
    const len = Math.hypot(d[0], d[1], d[2]);
    if (len < 1) continue;
    const x: Vec3 = [d[0] / len, d[1] / len, d[2] / len];
    const z = unit(depth, sign);
    const y = cross(z, x);
    const om = mm(o);
    runs.push({ assetId: 'detail.mitre', jointId: j.id, origin: [om[0] + z[0] * PROUD, om[1] + z[1] * PROUD, om[2] + z[2] * PROUD], x, y, z, length: len / 1000, partId: a.id });
  }
  return runs;
}
